import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGet, apiPatch, apiPost, errOf } from '../../lib/api';
import CommitteeForm, { blankCommittee, type CommitteeFormValues } from '../../components/CommitteeForm';
import CommitteeMembersPanel, { type MemberLite, type RoleLite } from '../../components/CommitteeMembersPanel';

interface Committee {
  id: string;
  code: string | null;
  kind: string;
  name_zh_hk: string;
  name_zh_cn: string;
  name_en: string;
  sort_order: number;
  status: string;
  member_count?: number;
}

const KINDS = ['board', 'functional', 'working'];
const btn: CSSProperties = { padding: '4px 10px', borderRadius: 8, border: '1px solid var(--color-divider)', background: 'var(--color-card)', fontSize: 'var(--font-size-tiny)', cursor: 'pointer' };
const btnPrimary: CSSProperties = { ...btn, background: 'var(--color-primary)', color: 'var(--color-on-primary)' };

/** 委員會管理（admin；decisions/009）—— 列表（三層分組）／新增／編輯 ＋ 邀請會員加入 */
export default function AdminCommitteesPage() {
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState<Committee[]>([]);
  const [roles, setRoles] = useState<RoleLite[]>([]);
  const [members, setMembers] = useState<MemberLite[]>([]);
  const [form, setForm] = useState<CommitteeFormValues | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [openId, setOpenId] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    const [c, r, m] = await Promise.all([
      apiGet<{ committees: Committee[] }>('/api/committee/committees'),
      apiGet<{ roles: RoleLite[] }>('/api/committee/roles'),
      apiGet<{ members: MemberLite[] }>('/api/members'),
    ]);
    if (c.ok && c.data) setRows(c.data.committees);
    if (r.ok && r.data) setRoles(r.data.roles);
    if (m.ok && m.data) setMembers(m.data.members);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const kindName = (k: string): string =>
    t(k === 'board' ? 'kind_board' : k === 'working' ? 'kind_working' : 'kind_functional');
  const nameOf = (c: Committee): string =>
    i18n.language === 'en' ? c.name_en : i18n.language === 'zh-CN' ? c.name_zh_cn : c.name_zh_hk;
  const roleName = (code: string): string => {
    const r = roles.find((x) => x.code === code);
    if (!r) return code;
    return i18n.language === 'en' ? r.name_en : i18n.language === 'zh-CN' ? r.name_zh_cn : r.name_zh_hk;
  };

  async function save() {
    if (!form) return;
    setErr('');
    const body = { ...form, sort_order: Number(form.sort_order) || 100 };
    const r = editId
      ? await apiPatch(`/api/committee/committees/${editId}`, body)
      : await apiPost('/api/committee/committees', body);
    if (!r.ok) {
      setErr(errOf(r.data) || '儲存失敗');
      return;
    }
    setForm(null);
    setEditId(null);
    await load();
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold">{t('admin_committees')}</h2>
        <button
          type="button"
          style={btnPrimary}
          onClick={() => {
            setEditId(null);
            setForm({ ...blankCommittee });
            setErr('');
          }}
        >
          ＋ {t('committee_new')}
        </button>
      </div>

      {form && (
        <CommitteeForm
          form={form}
          setForm={setForm}
          err={err}
          kindName={kindName}
          onSave={() => void save()}
          onCancel={() => {
            setForm(null);
            setEditId(null);
          }}
        />
      )}

      {err && !form && (
        <p className="text-sm mb-2" style={{ color: 'var(--color-danger)' }}>
          {err}
        </p>
      )}

      {KINDS.map((kind) => {
        const list = rows.filter((c) => c.kind === kind);
        return (
          <section key={kind} className="mb-4">
            <h3 className="text-sm font-bold mb-1" style={{ color: 'var(--color-text-secondary)' }}>
              {kindName(kind)}
            </h3>
            <div style={{ background: 'var(--color-card)', borderRadius: 12, overflow: 'hidden', boxShadow: 'var(--shadow-card)' }}>
              {list.length === 0 ? (
                <p className="p-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  {t('empty_none')}
                </p>
              ) : (
                list.map((c) => (
                  <div key={c.id} style={{ borderBottom: '1px solid var(--color-divider)' }}>
                    <div className="flex items-center justify-between gap-2 p-3">
                      <div>
                        <div className="font-semibold">{nameOf(c)}</div>
                        <div className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {c.code ?? '—'} ｜ {t('committee_count')} {c.member_count ?? 0}
                        </div>
                      </div>
                      <div style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          style={btn}
                          onClick={() => {
                            setEditId(c.id);
                            setErr('');
                            setForm({
                              kind: c.kind,
                              name_zh_hk: c.name_zh_hk,
                              name_zh_cn: c.name_zh_cn,
                              name_en: c.name_en,
                              code: c.code ?? '',
                              sort_order: String(c.sort_order),
                            });
                          }}
                        >
                          {t('btn_edit')}
                        </button>{' '}
                        <button type="button" style={btn} onClick={() => setOpenId(openId === c.id ? '' : c.id)}>
                          {t('committee_members')}
                        </button>
                      </div>
                    </div>
                    {openId === c.id && (
                      <CommitteeMembersPanel
                        committeeId={c.id}
                        members={members}
                        roles={roles}
                        roleName={roleName}
                        onChanged={() => void load()}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
